import FloatingLabelSelect from '@/components/atoms/FloatingLabelSelect';
import FloatingLabelInput from '@/components/atoms/FloatingLabelInput';
import CreatorNameSection from './CreatorNameSection';
import useToast from '@/hooks/useToast';
import { checkContentCreatorSocialLink } from '@/services/api/requests/contentCreatorApplications';
import FilePicker from '@/components/atoms/FilePicker';
import InlineAddTextField from '@/components/atoms/InlineAddTextField';
import { MAX_CHARS_IN_BIO, MIN_CHARS_IN_BIO } from '@/constants';
import { LOCAL_US_STATES, MAX_IMAGE_SIZE } from '@/constants/stringVars';
import { primary, text } from '@/theme/colors/palettes';
import {
  Box,
  Button,
  Checkbox,
  HStack,
  IconButton,
  Image,
  Input,
  SimpleGrid,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm, useWatch } from 'react-hook-form';
import ContentFocusTile, { ContentFocusValue } from './ContentFocusTile';
import svgFiles from '@/assets/svg/svgFiles';
import { useNavigate } from '@tanstack/react-router';
import {
  CreatorApplicationFormData,
  CreatorApplicationPreviewData,
  useCreatorApplicationStore,
} from '@/store/useCreatorApplicationStore';
import { contentFocusOptions, creatorApplicationSchema } from './creatorSchema';
import useContentCreatorApplicationPhotoUpload from '@/hooks/contentcreator/useContentCreatorApplicationPhotoUpload';
import { useAuthContext } from '@/context/auth/useAuthContext';
import { useAppStore } from '@/store/useAppStore';
import SvgIcon from '@/components/atoms/SvgIcon';
import svgIcons from '@/assets/svg/icons';
import { buildCreatorSocialLinks, creatorSocialMediaFields, getCreatorSocialUsername } from './creatorSocialLinks';

const capitalize = (value: string) => `${value.charAt(0).toUpperCase()}${value.slice(1)}`;

const QualificationInput = ({
  value,
  onChange,
  onSubmit,
  onDelete,
}: {
  value?: string;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  onDelete: () => void;
}) => (
  <InlineAddTextField
    placeholder={'Add Qualification'}
    minChars={2}
    maxChars={120}
    minHeight={'54px'}
    initialSubmittedValue={value}
    isEditableSubmittedValue
    onChange={onChange}
    onSubmit={onSubmit}
    onDeleteSubmitted={onDelete}
  />
);

const createQualificationState = (qualifications: string[] = []) => {
  const columns: number[][] = [[], []];
  const values: Record<number, string> = {};
  const submittedIds = new Set<number>();
  let nextId = 0;

  qualifications.forEach((qualification, index) => {
    const qualificationId = nextId++;
    columns[index % 2].push(qualificationId);
    values[qualificationId] = qualification;
    submittedIds.add(qualificationId);
  });

  columns.forEach((column) => {
    column.push(nextId++);
  });

  return { columns, values, submittedIds, nextId };
};

type CreatorApplicationFormProps = {
  applicantName?: CreatorApplicationPreviewData['applicantName'];
};

const CreatorApplicationForm = ({ applicantName }: CreatorApplicationFormProps) => {
  const USStates = LOCAL_US_STATES;
  const navigate = useNavigate();
  const toast = useToast();
  const publicCredentials = useCreatorApplicationStore((state) => state.publicCredentials);

  const { isAuthenticated } = useAuthContext();
  const user = useAppStore((state) => state.user);

  const previewData = useCreatorApplicationStore((state) => state.previewData);
  const setPreviewData = useCreatorApplicationStore((state) => state.setPreviewData);

  const { isPending: isUploadingPhoto, mutate: uploadContentCreatorApplicationPhoto } =
    useContentCreatorApplicationPhotoUpload();

  const profilePhotoInputRef = useRef<HTMLInputElement>(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState<string | null>(
    previewData?.profilePhotoPreview ?? null,
  );

  const initialQualificationState = createQualificationState(previewData?.qualifications);
  const [qualificationColumns, setQualificationColumns] = useState<number[][]>(initialQualificationState.columns);
  const [qualificationValues, setQualificationValues] = useState<Record<number, string>>(
    initialQualificationState.values,
  );

  const nextQualificationIdRef = useRef(initialQualificationState.nextId);
  const submittedQualificationIdsRef = useRef<Set<number>>(initialQualificationState.submittedIds);

  const {
    control,
    register,
    setError,
    clearErrors,
    getValues,
    trigger,
    setValue,
    reset,
    handleSubmit,
    formState: { errors, isSubmitting, touchedFields, submitCount },
  } = useForm<CreatorApplicationFormData>({
    mode: 'onChange',
    resolver: yupResolver(creatorApplicationSchema),
    context: { isExternalApplicant: !isAuthenticated },
    defaultValues: {
      profilePhoto: previewData?.profilePhoto ?? null,
      creatorName: previewData?.creatorName ?? '',
      keepNameSameAsRealName: previewData?.keepNameSameAsRealName ?? true,
      keepPhotoSameAsMemberPhoto: isAuthenticated && (previewData?.keepPhotoSameAsMemberPhoto ?? false),
      existingProfilePhotoUrl: previewData?.existingProfilePhotoUrl ?? null,
      aboutMe: previewData?.aboutMe ?? '',
      city: previewData?.city ?? (isAuthenticated ? (user?.user_metadata.city ?? '') : ''),
      zipCode: previewData?.zipCode ?? (isAuthenticated ? (user?.user_metadata.zip ?? '') : ''),
      state: previewData?.state ?? (isAuthenticated ? (user?.user_metadata.state ?? '') : ''),
      socialMediaHandles: creatorSocialMediaFields.map((_, index) =>
        getCreatorSocialUsername(previewData?.socialMediaHandles[index], index),
      ),
      qualifications: previewData?.qualifications ?? [],
      contentFocus: previewData?.contentFocus ?? [],
    },
  });

  const profilePhoto = useWatch({ control, name: 'profilePhoto' });
  const keepRealName = useWatch({ control, name: 'keepNameSameAsRealName' }) ?? true;
  const legalName = isAuthenticated ? user?.user_metadata : (applicantName ?? previewData?.applicantName);
  const realName = [legalName?.firstName, legalName?.lastName].filter(Boolean).join(' ');
  const keepPhotoSameAsMemberPhoto = useWatch({ control, name: 'keepPhotoSameAsMemberPhoto' });
  const keepMemberPhoto = isAuthenticated && keepPhotoSameAsMemberPhoto;
  const memberPhoto = user?.user_metadata.profilePicture;
  const displayedPhoto = keepMemberPhoto ? memberPhoto : profilePhotoPreview;
  const aboutMe = useWatch({ control, name: 'aboutMe' });
  const city = useWatch({ control, name: 'city' });
  const zipCode = useWatch({ control, name: 'zipCode' });
  const state = useWatch({ control, name: 'state' });
  const socialMediaHandles = useWatch({ control, name: 'socialMediaHandles' });

  const selectedContentFocus = useWatch({ control, name: 'contentFocus' });
  const contentFocusSelected = selectedContentFocus.length > 0;
  const hasSocialMediaHandle = socialMediaHandles?.some((handle) => Boolean(handle?.trim())) ?? false;

  const aboutMeLength = aboutMe.trim().length;
  const aboutMeValid = !errors.aboutMe && aboutMeLength >= MIN_CHARS_IN_BIO && aboutMeLength <= MAX_CHARS_IN_BIO;

  const canContinue = !isSubmitting && !isUploadingPhoto;
  const showFieldError = (field: keyof CreatorApplicationFormData) => submitCount > 0 || Boolean(touchedFields[field]);

  useEffect(() => {
    trigger(['city', 'zipCode', 'state']);
  }, [city, state, zipCode, trigger]);

  useEffect(() => {
    if (previewData) {
      reset({
        profilePhoto: previewData.profilePhoto,
        creatorName: previewData.creatorName ?? '',
        keepNameSameAsRealName: previewData.keepNameSameAsRealName ?? true,
        keepPhotoSameAsMemberPhoto: isAuthenticated && (previewData.keepPhotoSameAsMemberPhoto ?? false),
        existingProfilePhotoUrl: previewData.existingProfilePhotoUrl ?? null,
        aboutMe: previewData.aboutMe,
        city: previewData.city,
        zipCode: previewData.zipCode,
        state: previewData.state,
        socialMediaHandles: creatorSocialMediaFields.map((_, index) =>
          getCreatorSocialUsername(previewData.socialMediaHandles[index], index),
        ),
        qualifications: previewData.qualifications,
        contentFocus: previewData.contentFocus,
      });
      setProfilePhotoPreview(previewData.profilePhotoPreview);

      const restoredQualificationState = createQualificationState(previewData.qualifications);
      setQualificationColumns(restoredQualificationState.columns);
      setQualificationValues(restoredQualificationState.values);
      nextQualificationIdRef.current = restoredQualificationState.nextId;
      submittedQualificationIdsRef.current = restoredQualificationState.submittedIds;

      void trigger();
      return;
    }

    if (!isAuthenticated || !user) return;

    setValue('city', user.user_metadata.city ?? '');
    setValue('zipCode', user.user_metadata.zip ?? '');
    setValue('state', user.user_metadata.state ?? '');
  }, [isAuthenticated, previewData, reset, setValue, trigger, user]);

  const toggleContentFocus = (value: ContentFocusValue) => {
    const nextContentFocus = selectedContentFocus.includes(value)
      ? selectedContentFocus.filter((selectedValue) => selectedValue !== value)
      : [...selectedContentFocus, value];

    setValue('contentFocus', nextContentFocus, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const setProfilePhoto = (file: File | null) => {
    if (keepMemberPhoto) return;
    setValue('profilePhoto', file, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    if (file) {
      setValue('existingProfilePhotoUrl', null, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      uploadContentCreatorApplicationPhoto(file);
    }
  };

  const resetProfilePhoto = () => {
    setProfilePhoto(null);
    setValue('existingProfilePhotoUrl', null, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setProfilePhotoPreview(null);

    if (profilePhotoInputRef.current) {
      profilePhotoInputRef.current.value = '';
    }
  };

  const aboutMeRegisterProps = register('aboutMe');

  const cityRegisterProps = register('city');
  const zipCodeRegisterProps = register('zipCode');
  const stateRegisterProps = register('state');

  const updateQualificationValue = (qualificationId: number, value: string) => {
    if (!submittedQualificationIdsRef.current.has(qualificationId)) return;

    setQualificationValues((currentValues) => ({
      ...currentValues,
      [qualificationId]: value,
    }));
  };

  const addQualificationBelow = (columnIndex: number, qualificationId: number, value: string) => {
    if (submittedQualificationIdsRef.current.has(qualificationId)) return;

    submittedQualificationIdsRef.current.add(qualificationId);
    setQualificationValues((currentValues) => ({
      ...currentValues,
      [qualificationId]: value,
    }));

    const newQualificationId = nextQualificationIdRef.current;
    nextQualificationIdRef.current += 1;

    setQualificationColumns((currentColumns) =>
      currentColumns.map((column, currentColumnIndex) => {
        if (currentColumnIndex !== columnIndex) return column;

        const submittedQualificationIndex = column.indexOf(qualificationId);

        if (submittedQualificationIndex < 0) return column;

        const nextColumn = [...column];
        nextColumn.splice(submittedQualificationIndex + 1, 0, newQualificationId);

        return nextColumn;
      }),
    );
  };

  const removeQualification = (columnIndex: number, qualificationId: number) => {
    submittedQualificationIdsRef.current.delete(qualificationId);
    setQualificationValues((currentValues) => {
      const { [qualificationId]: _removedQualification, ...nextValues } = currentValues;

      return nextValues;
    });

    setQualificationColumns((currentColumns) =>
      currentColumns.map((column, currentColumnIndex) => {
        if (currentColumnIndex !== columnIndex) return column;

        const nextColumn = column.filter((currentQualificationId) => currentQualificationId !== qualificationId);

        return nextColumn.length > 0 ? nextColumn : [nextQualificationIdRef.current++];
      }),
    );
  };

  const submitCreatorApplication = async (values: CreatorApplicationFormData) => {
    if (keepMemberPhoto && !memberPhoto) {
      setError('profilePhoto', { type: 'validate', message: 'Profile photo is required.' });
      return;
    }
    clearErrors('socialMediaHandles');
    if (!isAuthenticated && (!publicCredentials?.email || !publicCredentials.token)) {
      toast({
        status: 'error',
        title: 'Unable to check social handles',
        description: 'Reopen your application link and try again.',
      });
      return;
    }
    const links = buildCreatorSocialLinks(values.socialMediaHandles);
    const results = await Promise.allSettled(
      links.map((link) => checkContentCreatorSocialLink(link, isAuthenticated ? undefined : publicCredentials!)),
    );
    // Ignore results if a handle changed while the requests were in flight.
    if (JSON.stringify(links) !== JSON.stringify(buildCreatorSocialLinks(getValues('socialMediaHandles')))) {
      toast({
        status: 'info',
        title: 'Social handles changed',
        description: 'Click Continue again to check your updated handles.',
      });
      return;
    }
    let hasDuplicate = false;
    results.forEach((result, index) => {
      if (result.status !== 'fulfilled' || !result.value.exists) return;
      const fieldIndex = creatorSocialMediaFields.findIndex((field) => field.platform === links[index].platform);
      setError(
        `socialMediaHandles.${fieldIndex}`,
        {
          type: 'server',
          message: 'This social handle is already linked to another creator application.',
        },
        { shouldFocus: !hasDuplicate },
      );
      hasDuplicate = true;
    });
    if (results.some((result) => result.status === 'rejected')) {
      toast({
        status: 'error',
        title: 'Unable to check social handles',
        description: 'Please try again. All handles must be checked before continuing.',
      });
      return;
    }
    if (hasDuplicate) return;

    const qualifications = qualificationColumns
      .flat()
      .map((qualificationId) => qualificationValues[qualificationId]?.trim())
      .filter((qualification): qualification is string => Boolean(qualification));

    const valuesWithPreview = {
      ...values,
      qualifications,
      profilePhotoPreview,
      applicantName: applicantName ?? previewData?.applicantName,
    };

    setPreviewData(valuesWithPreview);
    navigate({ to: '/creator/apply/preview' });
  };

  return (
    <VStack
      minHeight={'100vh'}
      width={'100vw'}
      justifyContent={{ base: 'center', md: 'flex-start' }}
      paddingY={'130px'}
      paddingX={'24px'}
    >
      <Text variant={'loraTitle'} color={'white'} fontSize={'26px'} lineHeight={'32px'} fontWeight={'400'}>
        Creator Application
      </Text>

      <Text
        color={text.veryLightBlue}
        fontSize={'16px'}
        fontWeight={'600'}
        lineHeight={'18px'}
        textAlign={'center'}
        opacity={0.7}
      >
        Please provide the required application information.
      </Text>

      <VStack
        as={'form'}
        width={'100%'}
        maxWidth={'640px'}
        spacing={'30px'}
        marginTop={'20px'}
        onSubmit={handleSubmit(submitCreatorApplication)}
      >
        <VStack width={'100%'}>
          <HStack
            width={'100%'}
            minHeight={'140px'}
            backgroundColor={'white'}
            borderRadius={'10px'}
            boxShadow={'0px 2px 18px 0px #1A00B933'}
            padding={'20px'}
            justifyContent={'space-between'}
            spacing={'24px'}
          >
            <VStack alignItems={'flex-start'} spacing={'8px'}>
              <Text color={'text.darkBlue'} fontSize={'18px'} lineHeight={'23px'} fontWeight={'600'}>
                Creator Profile Photo
              </Text>
              <Text color={'text.mediumGray'} fontSize={'14px'} lineHeight={'17px'}>
                * not smaller than 100x100px and not larger than {MAX_IMAGE_SIZE}.
              </Text>
              <Text
                color={'extra.yellowStrong'}
                fontSize={'12px'}
                lineHeight={'16px'}
                fontWeight={'800'}
                display={showFieldError('profilePhoto') && errors.profilePhoto?.message ? 'block' : 'none'}
              >
                {errors.profilePhoto?.message ? String(errors.profilePhoto.message) : 'Profile photo is required.'}
              </Text>
            </VStack>

            <Box
              width={'120px'}
              height={'120px'}
              borderRadius={'50%'}
              backgroundColor={primary[100]}
              border={'4px solid white'}
              boxShadow={'0px 2px 28px 0px #1A00B926'}
              position={'relative'}
              flexShrink={0}
            >
              <Box
                width={'100%'}
                height={'100%'}
                borderRadius={'50%'}
                overflow={'hidden'}
                display={'flex'}
                alignItems={'center'}
                justifyContent={'center'}
                backgroundColor={primary[100]}
              >
                {displayedPhoto ? (
                  <Image
                    src={displayedPhoto}
                    alt={profilePhoto?.name ?? 'profile-photo-preview'}
                    width={'100%'}
                    height={'100%'}
                    objectFit={'cover'}
                  />
                ) : (
                  <VStack position={'relative'} width={'100%'} height={'100%'} justifyContent={'center'}>
                    <Image
                      alt="generic-profile"
                      src={svgFiles.genericUser}
                      height={10}
                      width={10}
                      style={{ width: 140, height: 140, position: 'absolute', top: -14, left: 0 }}
                      objectFit={'cover'}
                    />
                    <Button
                      variant={'continue'}
                      width={'92px'}
                      minWidth={'92px'}
                      height={'44px'}
                      minHeight={'44px'}
                      isDisabled={Boolean(keepMemberPhoto) || isUploadingPhoto}
                      onClick={() => profilePhotoInputRef.current?.click()}
                    >
                      Upload
                    </Button>
                  </VStack>
                )}
              </Box>

              {profilePhotoPreview && !keepMemberPhoto && (
                <IconButton
                  aria-label="Remove profile photo"
                  type="button"
                  icon={
                    <SvgIcon
                      iconPath={svgIcons.closeSmall}
                      color={text.darkBlue}
                      size={18}
                      boxHeight={16}
                      boxWidth={18}
                      style={{ marginLeft: 2, marginTop: 2 }}
                    />
                  }
                  position={'absolute'}
                  top={'-6px'}
                  right={'-6px'}
                  minWidth={'30px'}
                  height={'30px'}
                  borderRadius={'50%'}
                  backgroundColor={'extra.white'}
                  color={'text.darkBlue'}
                  boxShadow={'0px 2px 8px 0px #7C9EBC80'}
                  _hover={{ backgroundColor: 'background.lightBlue' }}
                  onClick={resetProfilePhoto}
                  zIndex={2}
                />
              )}
            </Box>

            {!keepMemberPhoto && (
              <FilePicker
                selectedFile={profilePhoto}
                setSelectedFile={setProfilePhoto}
                setPreview={setProfilePhotoPreview}
                inputRef={profilePhotoInputRef}
                fileMinHeightPx={100}
                fileMinWidthPx={100}
                fileSizeLimitInMBs={3}
                accept={'.png, .jpeg, .jpg'}
              />
            )}
          </HStack>

          {isAuthenticated && (
            <>
              <Text color={text.veryLightBlue} fontSize={'16px'} textAlign={'center'} opacity={0.7}>
                You can upload new photo or use one from your member profile, if so they will be synced in future.
              </Text>
              <Checkbox
                alignSelf={'flex-start'}
                isChecked={Boolean(keepMemberPhoto)}
                isDisabled={isUploadingPhoto || !memberPhoto}
                onChange={(event) => {
                  setValue('keepPhotoSameAsMemberPhoto', event.target.checked, {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true,
                  });
                  void trigger('profilePhoto');
                }}
                size={'lg'}
                colorScheme={'primary'}
                spacing={'12px'}
                padding={'12px 18px'}
                backgroundColor={'rgba(255, 255, 255, 0.2)'}
                boxShadow={'0px 2px 4px 0px #0041790D'}
              >
                <Text fontFamily={'Urbanist'} fontWeight={500} fontSize={'16px'} color={text.veryLightBlue}>
                  Keep same as my member photo
                </Text>
              </Checkbox>
            </>
          )}
        </VStack>

        <VStack width={'100%'} spacing={'18px'}>
          <Text color={'white'} fontSize={'18px'} lineHeight={'25px'} fontWeight={'700'}>
            About Me
          </Text>
          <Textarea
            placeholder={'Add your about me or bio here'}
            minHeight={'108px'}
            resize={'vertical'}
            className="custom-input"
            width={'100%'}
            maxLength={MAX_CHARS_IN_BIO}
            style={{ padding: '14px' }}
            _placeholder={{ color: 'background.blueGray' }}
            {...aboutMeRegisterProps}
          />
          <Text
            color={'extra.yellowStrong'}
            fontSize={'12px'}
            lineHeight={'16px'}
            fontWeight={'800'}
            visibility={showFieldError('aboutMe') && !aboutMeValid ? 'visible' : 'hidden'}
          >
            {errors.aboutMe?.message ? String(errors.aboutMe.message) : 'About Me is required.'}
          </Text>
        </VStack>

        <VStack width={'100%'}>
          <Text color={'white'} fontSize={'18px'} lineHeight={'25px'} fontWeight={'700'}>
            Location
          </Text>
          {isAuthenticated && (
            <Text
              color={text.veryLightBlue}
              fontSize={'16px'}
              lineHeight={'20px'}
              fontWeight={500}
              textAlign={'center'}
              marginBottom={'10px'}
              opacity={0.7}
            >
              Your location is pulled from your profile, if you have changed your location you can edit it now.
            </Text>
          )}
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={'20px'} width={'100%'}>
            <VStack width={'100%'} alignItems={'flex-start'} spacing={'6px'}>
              <FloatingLabelInput
                label={'City'}
                isInvalid={showFieldError('city') && Boolean(errors.city)}
                {...cityRegisterProps}
              />
              <Text
                color={'extra.yellowStrong'}
                fontSize={'12px'}
                lineHeight={'16px'}
                fontWeight={'800'}
                visibility={showFieldError('city') && errors.city?.message ? 'visible' : 'hidden'}
              >
                {errors.city?.message ? String(errors.city.message) : 'City is required when location is entered.'}
              </Text>
            </VStack>
            <VStack width={'100%'} alignItems={'flex-start'} spacing={'6px'}>
              <FloatingLabelInput
                label={'ZIP Code'}
                inputMode={'numeric'}
                isInvalid={showFieldError('zipCode') && Boolean(errors.zipCode)}
                {...zipCodeRegisterProps}
              />
              <Text
                color={'extra.yellowStrong'}
                fontSize={'12px'}
                lineHeight={'16px'}
                fontWeight={'800'}
                visibility={showFieldError('zipCode') && errors.zipCode?.message ? 'visible' : 'hidden'}
              >
                {errors.zipCode?.message
                  ? String(errors.zipCode.message)
                  : 'ZIP Code is required when location is entered.'}
              </Text>
            </VStack>
            <VStack width={'100%'} alignItems={'flex-start'} spacing={'6px'}>
              <FloatingLabelSelect
                label={'State'}
                value={state ?? ''}
                isInvalid={showFieldError('state') && Boolean(errors.state)}
                {...stateRegisterProps}
              >
                {(USStates ?? []).map((USState) => (
                  <option key={USState.code} value={USState.code}>
                    {USState.label}
                  </option>
                ))}
              </FloatingLabelSelect>
              <Text
                color={'extra.yellowStrong'}
                fontSize={'12px'}
                lineHeight={'16px'}
                fontWeight={'800'}
                visibility={showFieldError('state') && errors.state?.message ? 'visible' : 'hidden'}
              >
                {errors.state?.message ? String(errors.state.message) : 'State is required when location is entered.'}
              </Text>
            </VStack>
          </SimpleGrid>
        </VStack>

        <Controller
          name={'creatorName'}
          control={control}
          render={({ field, fieldState }) => (
            <CreatorNameSection
              realName={realName}
              creatorName={field.value ?? ''}
              keepNameSameAsRealName={keepRealName}
              onCreatorNameChange={field.onChange}
              onKeepNameSameAsRealNameChange={(checked) => {
                setValue('keepNameSameAsRealName', checked, { shouldDirty: true, shouldValidate: true });
                void trigger('creatorName');
              }}
              inputRef={field.ref}
              onBlur={field.onBlur}
              error={fieldState.isTouched || submitCount > 0 ? fieldState.error?.message : undefined}
              isDisabled={isSubmitting}
              headingColor={'white'}
            />
          )}
        />

        <VStack width={'100%'} spacing={'18px'}>
          <Text color={'white'} fontSize={'18px'} lineHeight={'25px'} fontWeight={'700'}>
            Social Media Handles
          </Text>
          <SimpleGrid columns={2} spacing={'14px'} rowGap={'14px'} width={'100%'}>
            {creatorSocialMediaFields.map(({ platform, validation }, index) => {
              const socialMediaHandleError =
                (submitCount > 0 || touchedFields.socialMediaHandles?.[index]) &&
                Array.isArray(errors.socialMediaHandles)
                  ? errors.socialMediaHandles[index]?.message
                  : undefined;
              return (
                <VStack key={platform} width={'100%'} alignItems={'flex-start'}>
                  <HStack className={'custom-input'} width={'100%'} minHeight={'54px'} spacing={'8px'}>
                    <Text flexShrink={0} fontSize={'14px'} fontWeight={800} whiteSpace={'nowrap'}>
                      {validation}
                    </Text>
                    <Input
                      variant={'unstyled'}
                      placeholder={`${capitalize(platform)} username`}
                      width={'100%'}
                      minWidth={0}
                      padding={0}
                      _placeholder={{ color: 'background.blueGray', opacity: 0.65 }}
                      {...register(`socialMediaHandles.${index}`, {
                        setValueAs: (value: string) => getCreatorSocialUsername(value, index),
                      })}
                    />
                  </HStack>
                  <Text
                    color={'extra.yellowStrong'}
                    fontSize={'12px'}
                    lineHeight={'16px'}
                    fontWeight={'800'}
                    display={socialMediaHandleError ? 'flex' : 'none'}
                    visibility={socialMediaHandleError ? 'visible' : 'hidden'}
                  >
                    {socialMediaHandleError ?? 'Must match the social media URL format.'}
                  </Text>
                </VStack>
              );
            })}
          </SimpleGrid>
          <Text
            color={'extra.yellowStrong'}
            fontSize={'12px'}
            lineHeight={'16px'}
            fontWeight={'800'}
            visibility={
              (submitCount > 0 || touchedFields.socialMediaHandles?.some(Boolean)) && !hasSocialMediaHandle
                ? 'visible'
                : 'hidden'
            }
          >
            {!Array.isArray(errors.socialMediaHandles) && errors.socialMediaHandles?.message
              ? String(errors.socialMediaHandles.message)
              : 'Add at least one social media handle.'}
          </Text>
        </VStack>

        <VStack width={'100%'} spacing={'18px'}>
          <VStack spacing={'6px'}>
            <Text color={'white'} fontSize={'18px'} lineHeight={'25px'} fontWeight={'700'} textAlign={'center'}>
              Qualifications or Certifications (optional)
            </Text>
            <Text
              color={text.veryLightBlue}
              opacity={0.7}
              fontSize={'16px'}
              lineHeight={'20px'}
              fontWeight={'600'}
              textAlign={'center'}
            >
              Example: certified peer support.
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={'20px'} width={'100%'}>
            {qualificationColumns.map((column, columnIndex) => (
              <VStack key={columnIndex} spacing={'20px'} width={'100%'}>
                {column.map((qualificationId) => (
                  <QualificationInput
                    key={qualificationId}
                    value={qualificationValues[qualificationId]}
                    onChange={(value) => updateQualificationValue(qualificationId, value)}
                    onSubmit={(value) => addQualificationBelow(columnIndex, qualificationId, value)}
                    onDelete={() => removeQualification(columnIndex, qualificationId)}
                  />
                ))}
              </VStack>
            ))}
          </SimpleGrid>
        </VStack>

        <VStack width={'100%'} spacing={'18px'}>
          <Text color={'white'} fontSize={'18px'} lineHeight={'25px'} fontWeight={'700'} textAlign={'center'}>
            Choose Content Focus
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={'14px'} width={'100%'}>
            {contentFocusOptions.map((option) => (
              <ContentFocusTile
                key={option.value}
                label={option.label}
                value={option.value}
                isSelected={selectedContentFocus.includes(option.value)}
                onToggle={toggleContentFocus}
              />
            ))}
          </SimpleGrid>
          <Text
            color={'extra.yellowStrong'}
            fontSize={'12px'}
            lineHeight={'16px'}
            fontWeight={'800'}
            visibility={showFieldError('contentFocus') && !contentFocusSelected ? 'visible' : 'hidden'}
          >
            Select at least one content focus.
          </Text>
        </VStack>

        <Button
          type={'submit'}
          variant={'continue'}
          width={'100%'}
          maxWidth={'320px'}
          marginTop={'10px'}
          isDisabled={!canContinue}
          isLoading={isSubmitting}
        >
          Continue
        </Button>
      </VStack>
    </VStack>
  );
};

export default CreatorApplicationForm;
